import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_SCHEME_URL, APP_STORE_URL } from "../config/app-links.config";

/**
 * Is APP_STORE_URL a real App Store listing?
 *
 * The app is not published yet, so this value is a stand-in. Redirecting to a
 * stand-in is strictly worse than not redirecting: it ejects the visitor off
 * the site to somewhere unrelated, with no explanation and no way back. Until
 * there is a listing the page says so and stays put.
 *
 * The same reasoning the Android branch already used, applied to the iOS
 * branch now that iOS has no listing either.
 *
 * The config's own fallback is rejected too. It is a real apps.apple.com URL
 * with a fake id, so a host check alone waves it through — and with no
 * REACT_APP_APP_STORE_URL set at all, that fallback is what ships.
 */
export const hasStoreListing = (url: string): boolean =>
  url.startsWith("https://apps.apple.com/") && !url.includes("id0000000000");

/**
 * iOS, as far as a web page can tell.
 *
 * iPadOS 13+ reports a desktop Safari user agent, so a touch count is the only
 * thing separating an iPad from a Mac.
 */
export const isIos = (userAgent: string, maxTouchPoints: number): boolean =>
  /iPad|iPhone|iPod/i.test(userAgent) ||
  (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);

/**
 * Which store, if any, this visitor should be sent to. Exported because the
 * user-agent branch is the only logic on this page and the only place a typo
 * is silent — the rest of the mechanism lives in the OS.
 *
 * iOS only this release. Android is not redirected anywhere: the one thing
 * worse than no Play listing is bouncing a phone to a store page that 404s.
 */
export const storeUrlFor = (
  userAgent: string,
  maxTouchPoints: number,
): string | null => {
  if (!hasStoreListing(APP_STORE_URL)) return null;
  return isIos(userAgent, maxTouchPoints) ? APP_STORE_URL : null;
};

/**
 * The scheme URL to fire for this visitor, or "" for nobody.
 *
 * Exported because OpenInApp fires it from inside the tap handler and has to
 * make the identical decision. Two copies of this gate would drift, and the
 * failure is silent in the direction that matters: a scheme fired at Android
 * or a desktop browser is an error dialog for a visitor who was only browsing.
 */
export const schemeUrlFor = (
  userAgent: string,
  maxTouchPoints: number,
): string => (isIos(userAgent, maxTouchPoints) ? APP_SCHEME_URL : "");

/**
 * Set in the router state by OpenInApp when it has already fired the scheme.
 */
type OpenAppNav = { schemeFired?: boolean } | null;

/**
 * How long the store fallback waits for the app to take over.
 *
 * Long enough that a cold launch wins the race, short enough that a visitor
 * without the app is not left on a page that is about to navigate. Only the
 * fallback waits — the app itself opens the moment the OS resolves the scheme.
 */
const SCHEME_TIMEOUT_MS = 1200;

/**
 * Served at /app, the path the app claims in
 * public/.well-known/apple-app-site-association.
 *
 * Reaching this page means the OS did NOT hand the URL to the app, which is
 * either because it is not installed or because the link was tapped on this
 * same host (iOS refuses those — see OpenInApp). The custom scheme separates
 * the two: it has no same-host rule, so it opens the app for the visitor who
 * has it, and the store link is what is left for the visitor who does not.
 */
const OpenApp = () => {
  const { t } = useTranslation();
  const schemeFired = (useLocation().state as OpenAppNav)?.schemeFired === true;

  useEffect(() => {
    const { userAgent, maxTouchPoints } = navigator;
    const store = storeUrlFor(userAgent, maxTouchPoints);
    const scheme = schemeUrlFor(userAgent, maxTouchPoints);

    // ponytail: iOS shows "Cannot Open Page" when nothing claims the scheme,
    // and there is no way to ask first. The fix is a Universal Link, which iOS
    // refuses from this host — so this is the price of the button working at
    // all. Revisit if the app is ever linked from a second host we control.
    //
    // Not fired again when the button already fired it inside the tap: a second
    // navigation to the same URL buys nothing, and on the phone without the app
    // it is a second "Cannot Open Page" to dismiss. The fallback below still
    // runs — firing is what was already done, waiting is not.
    if (scheme && !schemeFired) window.location.href = scheme;

    if (!store) return;

    // replace, not assign: back from the store should return to wherever the
    // visitor came from, not to this page and straight back out again.
    if (!scheme) {
      window.location.replace(store);
      return;
    }

    // The app opening backgrounds this tab. Redirecting regardless would leave
    // the App Store waiting behind the app, so closing the app drops the
    // visitor onto a listing for the app they were just in.
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.location.replace(store);
      }
    }, SCHEME_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [schemeFired]);

  // Rendered for everyone, including the phones that are about to be
  // redirected — the redirect can be blocked, and a blank page with no way
  // forward is worse than a store link the visitor did not need.
  return (
    <div
      data-testid="page-open-app"
      className="card mx-auto mt-12 max-w-md p-8 text-center"
    >
      <h1 className="mb-2 font-display text-2xl font-black">
        {t("openApp.title")}
      </h1>
      <p className="mb-6 text-ink-soft">
        {t(hasStoreListing(APP_STORE_URL) ? "openApp.body" : "openApp.notPublished")}
      </p>
      {hasStoreListing(APP_STORE_URL) && (
        <div className="flex flex-col items-center gap-3">
          <a className="btn-ghost" href={APP_STORE_URL}>
            {t("openApp.appStore")}
          </a>
        </div>
      )}
    </div>
  );
};

export default OpenApp;
